import React, { useEffect, useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../../api/config';
import InternationalPhoneField from '../forms/InternationalPhoneField';

interface FarmerFormProps {
    initialData?: any;
    onSubmit: (data: any) => Promise<void>;
    onCancel: () => void;
    isLoading: boolean;
    errorMessage?: string;
}

const toDateInputValue = (value: any): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10) || '';
    return date.toISOString().slice(0, 10);
};

const resolvePhotoUrl = (value: any): string => {
    if (!value) return '';
    if (value.startsWith('http')) return value;
    return `${API_URL}/${String(value).replace(/^\//, '')}`;
};

const FarmerForm: React.FC<FarmerFormProps> = ({ initialData, onSubmit, onCancel, isLoading, errorMessage }) => {
    const [formData, setFormData] = useState({
        full_name: '',
        phone: '',
        date_of_birth: '',
        gender: '',
        household_type: 'standard',
        status: true,
        photo: null as File | null,
        photo_preview: '',
        mobile_login_email: '',
        location_address: ''
    });

    useEffect(() => {
        if (!initialData) return;
        setFormData({
            full_name: initialData.full_name || '',
            phone: initialData.phone || '',
            date_of_birth: toDateInputValue(initialData.date_of_birth),
            gender: initialData.gender || '',
            household_type: ['champion', 'standard'].includes(initialData.household_type) ? initialData.household_type : 'standard',
            status: initialData.status !== false,
            photo: null,
            photo_preview: resolvePhotoUrl(initialData.photo_url || initialData.profile_url),
            mobile_login_email: initialData.mobile_login_email || '',
            location_address: initialData.location_address || initialData.address || ''
        });
    }, [initialData]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setFormData((prev) => ({
            ...prev,
            photo: file,
            photo_preview: URL.createObjectURL(file)
        }));
    };

    const uploadPhoto = async () => {
        if (!formData.photo) return formData.photo_preview || null;
        const token = localStorage.getItem('aaywa_token');
        const uploadFormData = new FormData();
        uploadFormData.append('photo', formData.photo);

        const response = await fetch(`${API_URL}/api/farmers/upload-photo?type=farmers`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: uploadFormData
        });

        if (!response.ok) return formData.photo_preview || null;
        const data = await response.json();
        return data.url || (data.path ? `${API_URL}/${data.path.replace(/^\//, '')}` : null) || formData.photo_preview || null;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const photoUrl = await uploadPhoto();
        await onSubmit({
            full_name: formData.full_name.trim(),
            phone: formData.phone.trim(),
            date_of_birth: formData.date_of_birth || null,
            gender: formData.gender,
            household_type: formData.household_type,
            status: formData.status,
            create_mobile_account: true,
            mobile_login_email: formData.mobile_login_email.trim() || null,
            photo_url: photoUrl,
            location_address: formData.location_address.trim() || null,
            cohort_id: null,
            vsla_id: null,
            crops: null,
            co_crops: null,
            plot_size_hectares: null,
            agronomist_id: null,
            field_facilitator_id: null
        });
    };

    return (
        <form onSubmit={handleSubmit} className="overflow-hidden rounded-2xl bg-white">
            <div className="border-b border-emerald-100 bg-gradient-to-r from-emerald-700 to-teal-700 p-6 text-white">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-100">Farmer profile</p>
                <h2 className="mt-2 text-2xl font-black">{initialData ? 'Edit farmer' : 'Add farmer'}</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-emerald-50">
                    Keep this profile simple. Farms, crops, maps, trees, agronomists, and field facilitators are assigned from Farm Maps and Traceability.
                </p>
            </div>

            <div className="grid gap-6 p-6 lg:grid-cols-[220px_1fr]">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <p className="text-sm font-black text-slate-800">Profile photo</p>
                    <div className="mt-4">
                        {formData.photo_preview ? (
                            <div className="relative inline-block">
                                <img
                                    src={formData.photo_preview}
                                    alt="Farmer preview"
                                    className="h-32 w-32 rounded-2xl border-4 border-white object-cover shadow"
                                />
                                <button
                                    type="button"
                                    onClick={() => setFormData((prev) => ({ ...prev, photo: null, photo_preview: '' }))}
                                    className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1.5 text-white shadow"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-white text-slate-400 hover:border-emerald-400 hover:text-emerald-600">
                                <PhotoIcon className="h-9 w-9" />
                                <span className="mt-2 text-xs font-bold">Upload</span>
                                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                            </label>
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    {initialData?.farmer_code ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                            <p className="text-sm font-black text-slate-800">Farmer code</p>
                            <p className="mt-1 text-xs leading-5 text-slate-500">
                                Auto-generated and unique within this workspace. It is not editable.
                            </p>
                            <div className="mt-4 inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-mono text-sm font-black tracking-wide text-slate-800">
                                {initialData.farmer_code}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-5">
                            <p className="text-sm font-black text-emerald-900">Farmer code</p>
                            <p className="mt-1 text-xs leading-5 text-emerald-800">
                                Auto-generated after saving, unique within this workspace. Example: <span className="font-mono font-black">ESANZA-FARM-0001</span>
                            </p>
                            <div className="mt-4 inline-flex items-center rounded-xl border border-dashed border-emerald-300 bg-white px-4 py-2.5 font-mono text-sm font-black tracking-wide text-slate-400">
                                Auto-generated
                            </div>
                        </div>
                    )}

                    <div className="grid gap-4 md:grid-cols-2">
                        <label className="block">
                            <span className="text-sm font-bold text-slate-700">Full name *</span>
                            <input
                                required
                                name="full_name"
                                value={formData.full_name}
                                onChange={handleChange}
                                placeholder="e.g., Lambert Ndacyayisaba"
                                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                            />
                        </label>
                        <InternationalPhoneField
                            label="Phone"
                            value={formData.phone}
                            onChange={(val) => setFormData((prev) => ({ ...prev, phone: val }))}
                            required
                            defaultCountry="RW"
                        />
                        <label className="block">
                            <span className="text-sm font-bold text-slate-700">Date of birth</span>
                            <input
                                name="date_of_birth"
                                type="date"
                                value={formData.date_of_birth}
                                onChange={handleChange}
                                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                            />
                        </label>
                        <label className="block">
                            <span className="text-sm font-bold text-slate-700">Gender</span>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                            >
                                <option value="">Select gender</option>
                                <option value="female">Female</option>
                                <option value="male">Male</option>
                                <option value="other">Other</option>
                            </select>
                        </label>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <label className="block">
                            <span className="text-sm font-bold text-slate-700">Farmer type</span>
                            <select
                                name="household_type"
                                value={formData.household_type}
                                onChange={handleChange}
                                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                            >
                                <option value="standard">Standard Farmer</option>
                                <option value="champion">Lead Farmer</option>
                            </select>
                        </label>
                        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                            <input
                                type="checkbox"
                                checked={formData.status}
                                onChange={(event) => setFormData((prev) => ({ ...prev, status: event.target.checked }))}
                                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span>
                                <span className="block text-sm font-black text-slate-800">Active farmer</span>
                                <span className="block text-xs text-slate-500">Inactive farmers remain stored but hidden from active operations.</span>
                            </span>
                        </label>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <p className="text-sm font-black text-slate-800">Farmer address</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                            Type the normal address, village, cell, sector, or nearby landmark. Exact farm boundaries are created separately in Farm Maps.
                        </p>
                        <div className="mt-4">
                            <label className="block">
                                <span className="text-sm font-bold text-slate-700">Address / village / landmark</span>
                                <input
                                    name="location_address"
                                    value={formData.location_address}
                                    onChange={handleChange}
                                    placeholder="e.g., Masaka sector, near cooperative office"
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                />
                            </label>
                        </div>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                        <p className="text-sm font-black text-emerald-950">Farmer mobile access</p>
                        <p className="mt-1 text-xs leading-5 text-emerald-800">
                            Access is created automatically. The farmer signs in using the registered phone number only.
                        </p>
                        <label className="mt-4 block">
                            <span className="text-sm font-bold text-slate-700">Email <span className="font-normal text-slate-400">(optional)</span></span>
                            <input
                                name="mobile_login_email"
                                type="email"
                                value={formData.mobile_login_email}
                                onChange={handleChange}
                                placeholder="farmer@example.com"
                                className="mt-2 w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                            />
                        </label>
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
                {errorMessage && (
                    <p className="mr-auto flex items-center text-sm font-bold text-red-600">
                        {errorMessage}
                    </p>
                )}
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isLoading}
                    className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-6 py-3 text-sm font-black text-white shadow-lg shadow-emerald-700/20 hover:bg-emerald-800 disabled:opacity-50"
                >
                    {isLoading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                    {isLoading
                        ? (initialData ? 'Updating...' : 'Creating...')
                        : (initialData ? 'Update farmer' : 'Create farmer')}
                </button>
            </div>
        </form>
    );
};

export default FarmerForm;
